/**
 * Determines the owning team for a failure event based on the experience task name,
 * error message, and optional attributes (e.g. actionKey).
 *
 * The rules are derived from the AIFC Top Baseline Errors report and use regex matching
 * to handle error messages that contain dynamic values (UUIDs, IDs, hashes, etc).
 *
 * @see https://hello.atlassian.net/wiki/spaces/ac8/pages/6822974693/Apr+13-14+2026+-+AIFC+Top+Baseline+Errors
 */
import { ExperienceName } from './constants';
import type { ExperienceNameType } from './constants';

export enum OwningTeam {
	AifcFoundations = 'AIFC Foundations',
	EditorAi = 'Editor AI',
	RovoChat = 'Rovo Chat',
	Whiteboards = 'Whiteboards',
	Databases = 'Databases',
	JiraAifc = 'Jira AIFC',
	Pulsar = 'Pulsar',
}

/** A rule that matches an error pattern and maps it to a team. */
interface ErrorPatternRule {
	pattern: RegExp;
	team: OwningTeam;
}

/** A rule scoped to one or more specific experiences. */
interface ExperienceErrorPatternRule extends ErrorPatternRule {
	experiences: ExperienceNameType[];
}

/**
 * Step 1 — Experience-specific error rules.
 * These are checked first and only when the experience name matches.
 */
const RULES_FOR_SPECIFIC_EXPERIENCES: ExperienceErrorPatternRule[] = [
	{
		pattern: /Something went wrong on Rovo's end. Please try sending your message again./i,
		team: OwningTeam.AifcFoundations,
		experiences: [ExperienceName.AIFC_GENERATE_CONTENT],
	},
	{
		pattern:
			/The ConfluenceCreationTool was invoked, but valid content was not created for action key:.*whiteboard/i,
		team: OwningTeam.Whiteboards,
		experiences: [ExperienceName.AIFC_GENERATE_CONTENT],
	},
	{
		pattern:
			/The ConfluenceCreationTool was invoked, but valid content was not created for action key:.*database/i,
		team: OwningTeam.Databases,
		experiences: [ExperienceName.AIFC_GENERATE_CONTENT],
	},
	{
		pattern:
			/The ConfluenceCreationTool was invoked, but valid content was not created for action key:.*page/i,
		team: OwningTeam.AifcFoundations,
		experiences: [ExperienceName.AIFC_GENERATE_CONTENT],
	},
	{
		pattern:
			/The orchestrator reached its depth limit and the ConfluenceCreationTool was not invoked./i,
		team: OwningTeam.AifcFoundations,
		experiences: [ExperienceName.AIFC_GENERATE_CONTENT],
	},
	{
		pattern: /Could not create content of type \w+ in the Confluence staging area./i,
		team: OwningTeam.AifcFoundations,
		experiences: [
			ExperienceName.AIFC_GENERATE_CONTENT,
			ExperienceName.AIFC_GENERATE_CONTENT_IN_CHAT,
		],
	},
	{
		pattern:
			/Message action error: [Code 400] convoai.rest.bad.request - Failed to update page: missing pageId and ADF content for actionKey:.*/i,
		team: OwningTeam.AifcFoundations,
		experiences: [ExperienceName.AIFC_UPDATE_CONTENT],
	},
	{
		pattern:
			/Message action error: [Code 400] convoai.rest.bad.request - Failed to update page: missing pageId and ADF content for actionKey:.*/i,
		team: OwningTeam.EditorAi,
		experiences: [ExperienceName.UPDATE_CONTENT_IN_CHAT],
	},
	{
		pattern: /AIFC stream received edit tags but no elements were affected/i,
		team: OwningTeam.Whiteboards,
		experiences: [ExperienceName.AIFC_UPDATE_CONTENT],
	},
];

/**
 * Step 2 — Global error rules that apply across all experiences.
 */
const GLOBAL_ERROR_MESSAGE_RULES: ErrorPatternRule[] = [
	{
		pattern: /network error/i,
		team: OwningTeam.RovoChat,
	},
	{
		pattern: /An error occurred. Please try again later./i,
		team: OwningTeam.RovoChat,
	},
	{
		pattern: /Failed to fetch/i,
		team: OwningTeam.RovoChat,
	},
	{
		pattern: /An unknown error occurred/i,
		team: OwningTeam.RovoChat,
	},
	{
		pattern:
			/\[Code 403\] {"code":403,"message":"You're unable to access content because your IP address is not listed in the IP allowlist. Contact your admin for help."}/i,
		team: OwningTeam.RovoChat,
	},
	{
		pattern: /AIFC stream received only preamble but no <todo_list>, SVG or whiteboards XML.*/i,
		team: OwningTeam.Whiteboards,
	},
	{
		pattern: /No sizing converter defined for element type 'mindmap'/i,
		team: OwningTeam.Whiteboards,
	},
	{
		pattern: /AIFC stream received edit tags but no elements were affected/i,
		team: OwningTeam.Whiteboards,
	},
	{
		pattern: /Could not find database:/i,
		team: OwningTeam.Databases,
	},
];

/**
 * Step 3 — Default team ownership per experience.
 * Used as a fallback when no error-pattern rule matches.
 */
const DEFAULT_TEAM_FOR_EXPERIENCE = new Map<ExperienceNameType, OwningTeam>([
	[ExperienceName.AIFC_FINALIZE_CONTENT, OwningTeam.AifcFoundations],
	[ExperienceName.AIFC_ALL_DATA_HANDLED, OwningTeam.AifcFoundations],
	[ExperienceName.AIFC_GENERATE_CONTENT, OwningTeam.AifcFoundations],
	[ExperienceName.AIFC_GENERATE_CONTENT_IN_CHAT, OwningTeam.AifcFoundations],
	[ExperienceName.AIFC_INITIAL_DATA_RECEIVED, OwningTeam.AifcFoundations],
	[ExperienceName.AIFC_MODAL_SESSION, OwningTeam.AifcFoundations],
	[ExperienceName.AIFC_UPDATE_CONTENT, OwningTeam.AifcFoundations],
	[ExperienceName.UPDATE_CONTENT_IN_CHAT, OwningTeam.EditorAi],
	[ExperienceName.PREVIEW_CARD_RENDER, OwningTeam.AifcFoundations],
	[ExperienceName.PREVIEW_CONTENT, OwningTeam.AifcFoundations],
	[ExperienceName.SEND_MESSAGE, OwningTeam.RovoChat],
	[ExperienceName.SEND_TASK_MODE_MESSAGE, OwningTeam.RovoChat],
	[ExperienceName.SEND_CONFIRMED_TOOLS_TASK_MODE_MESSAGE, OwningTeam.RovoChat],
	[ExperienceName.LOAD_AUTO_SUGGEST_SKILLS_LEGACY, OwningTeam.RovoChat],
	[ExperienceName.LOAD_AUTO_SUGGEST_SKILLS_UNIFIED, OwningTeam.RovoChat],
	[ExperienceName.LOAD_CHAT_PROMPT_INPUT_SKILLS, OwningTeam.RovoChat],
	[ExperienceName.LOAD_INLINE_CHAT_PROMPT_INPUT_SKILLS, OwningTeam.RovoChat],
	[ExperienceName.JIRA_SAVE_DRAFT_WORK_ITEM, OwningTeam.JiraAifc],
	[ExperienceName.JIRA_CREATE_WORK_ITEMS_BULK, OwningTeam.JiraAifc],
	[ExperienceName.ROVO_SKILLS_PLAN_WORK_ITEM_CREATE, OwningTeam.JiraAifc],
	[ExperienceName.ROVO_SKILLS_EXECUTION_WORK_ITEM_CREATE, OwningTeam.JiraAifc],
	[ExperienceName.SHARE_CONVERSATION_SESSION, OwningTeam.RovoChat],
	[ExperienceName.MANAGE_CONVERSATION_SHARES, OwningTeam.RovoChat],
	[ExperienceName.VIEW_CONVERSATION_SHARE, OwningTeam.RovoChat],
	[ExperienceName.ROVO_SKILLS_PLAN_WORK_ITEM_UPDATE, OwningTeam.Pulsar],
	[ExperienceName.ROVO_SKILLS_EXECUTION_WORK_ITEM_UPDATE, OwningTeam.Pulsar],
]);

/**
 * Determines the owning team for a failure event according to this 3-step process:
 * 1. Try experience-specific error-pattern rules.
 * 2. Try global error-pattern rules.
 * 3. Fall back to the default team for the experience.
 *
 * @param errorMessage - The error message from the failure event
 * @param experienceName - The experience name that failed (optional)..
 * @returns The owning team name, or undefined if no rule matches
 */
export const getOwningTeam = (
	errorMessage: string,
	experienceName: ExperienceNameType,
): OwningTeam | undefined => {
	if (!errorMessage) {
		return DEFAULT_TEAM_FOR_EXPERIENCE.get(experienceName);
	}

	// Step 1: Experience-specific error-pattern rules
	if (experienceName) {
		for (const rule of RULES_FOR_SPECIFIC_EXPERIENCES) {
			if (rule.experiences.includes(experienceName) && rule.pattern.test(errorMessage)) {
				return rule.team;
			}
		}
	}

	// Step 2: Global error-pattern rules
	for (const rule of GLOBAL_ERROR_MESSAGE_RULES) {
		if (rule.pattern.test(errorMessage)) {
			return rule.team;
		}
	}

	// Step 3: Default team for this experience
	if (experienceName) {
		return DEFAULT_TEAM_FOR_EXPERIENCE.get(experienceName);
	}

	return undefined;
};
